/**
 * Cart progress bar for the tiered discount (theme settings > Cart Tiered Discount).
 *
 * How the tiers are evaluated (see README.md, "Cart tiered discount"):
 * - Which products count: without include lists every product counts, with include lists only the products from the
 *   included collections plus the included products; excluded collections/products never count (theme settings
 *   "Products that count towards the tiers"). Collection handles are resolved to product ids from
 *   /collections/<handle>/products.json (250 per page, cached per session). Free gifts never count. Free shipping tiers
 *   always count the whole cart (only the exclusions apply).
 * - The line price counts after the bundle discounts (Pick & Mix, paste packs, from the line item properties) but before
 *   any other discount Shopify applied, in particular the tier discount itself: otherwise reaching the threshold would
 *   lower the total again and un-reach the tier. Every tier has its own total (its eligible products).
 * - A tier is reached when its total meets the goal AND, when a Shopify discount title is configured for the tier, that
 *   discount is actually present in the cart (line or cart level discount allocations from cart.js).
 * - The bar shows the reached tiers plus the progress towards the next tier, measured on that next tier's total.
 */
class CartProgressBar extends HTMLElement {
  // Shared between all instances (section + cart drawer) so they never add/remove free products at the same time
  static _processingFreeProducts = false;

  // Shared between all instances: collection handle -> Promise<Set<productId> | null>, one products.json fetch per collection
  static _collectionProducts = new Map();

  // Shopify escapes discount titles in cart.js ("Pick&amp;Mix"), so titles are compared decoded, trimmed and case-insensitive
  static normalizeDiscountTitle(title) {
    return String(title || '').replace(/&amp;/g, '&').trim().toLowerCase();
  }

  connectedCallback() {
    this.options = JSON.parse(this.dataset.options);
    this.tiers = (this.options.tiers || [])
      .filter(tier => tier.goal > 0)
      .sort((a, b) => a.goal - b.goal)
      .map(tier => ({ ...tier, discountTitle: CartProgressBar.normalizeDiscountTitle(tier.discountTitle) }));

    // Product filters (theme settings "Products that count towards the tiers")
    const list = value => (Array.isArray(value) ? value.filter(Boolean) : []);
    this.collectionsToInclude = list(this.options.collectionsToInclude);
    this.productIdsToInclude = list(this.options.productIDsToInclude);
    this.collectionsToExclude = list(this.options.collectionsToExclude);
    // Set of the product ids that count towards the tiers, null = every product counts
    this.eligibleProductIds = null;
    // Set of the product ids that never count (grows with the excluded collections once resolved)
    this.excludedProductIds = new Set(list(this.options.productIDsToExclude));

    // Without tiers there is nothing to render or track
    if (!this.tiers.length) return;

    // Every tier takes an equal part of the bar
    this.tierWidthPercent = 100 / this.tiers.length;
    this.wrapper = this.closest('[data-cart-progress-bar-wrapper]');
    this.track = this.querySelector('[data-tier-track]');
    this.nextTierText = this.querySelector('[data-next-tier]');
    this.thresholdsContainer = this.querySelector('[data-tier-thresholds]');
    this._observer = null;
    this._initialized = false;

    this.renderThresholdMarkers();
    // The eligible products per tier must be known before the first calculation
    this.resolveEligibleProducts()
      .then(() => this.updateProgress(null))
      .then(() => {
        this._initialized = true;
      });

    this.cartUpdatedHandler = event => this.updateProgress(event.detail);
    window.addEventListener('cart:updated', this.cartUpdatedHandler);

    if (this.options.context === 'section') {
      // On product pages the sticky add to cart bar takes the bottom of the screen
      this.isBottomBar = this.options.layout === 'bottom' && !document.body.classList.contains('template-product');
      this.updateSectionHeight();

      if (this.isBottomBar) {
        this.resizeHandler = debounce(() => {
          this.updateSectionHeight();
          this.initFixedObserver();
        }, 50);
        window.addEventListener('resize', this.resizeHandler);
        this.initFixedObserver();
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('cart:updated', this.cartUpdatedHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    this._observer?.disconnect();
    if (this.isBottomBar) this.setFixed(false);
  }

  /**
   * The height of the bar is used to reserve space below the page content when the bar is fixed at the bottom
   */
  updateSectionHeight() {
    document.documentElement.style.setProperty('--section-height', `${this.offsetHeight}px`);
  }

  /**
   * Shows the bar fixed at the bottom once the user scrolls past the position of the section.
   * The wrapper has no height (the bar itself is position: fixed), so the observer only reports where the section sits in the page.
   */
  initFixedObserver() {
    if (this._observer || window.innerHeight <= 480) return;

    this._observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.setFixed(entry.boundingClientRect.top <= 0);
      });
    });
    this._observer.observe(this.wrapper);
  }

  setFixed(isFixed) {
    this.wrapper.classList.toggle('is-fixed', isFixed);
    // The CSS reserves space below #MainContent while this class is present
    document.documentElement.classList.toggle('cart-progress-bar-is-fixed', isFixed);
  }

  renderThresholdMarkers() {
    if (!this.thresholdsContainer) return;

    this.thresholdsContainer.innerHTML = '';
    const isSingleTier = this.classList.contains('cart-progress-bar--single');

    this.tiers.forEach((tier, index) => {
      let type = 'product';
      if (tier.freeShipping) type = 'free-shipping';
      else if (tier.discount) type = 'percentage';

      const marker = document.createElement('div');
      marker.className = `cart-progress-bar__threshold cart-progress-bar__threshold--${type}`;
      marker.setAttribute('data-threshold', tier.goal);

      // A single tier marker follows the progress (see the stylesheet in snippets/cart-progress-bar.liquid)
      if (!isSingleTier) marker.style.left = `${(index + 1) * this.tierWidthPercent}%`;

      if ((this.options.discountType === 'product' || tier.freeShipping) && tier.image) {
        marker.setAttribute('data-type', 'icon');
        const img = document.createElement('img');
        img.src = tier.image;
        img.alt = '';
        img.className = 'cart-progress-bar__threshold-icon';
        img.width = 24;
        img.height = 24;
        if (tier.freeShipping) img.setAttribute('data-svg', true);
        marker.appendChild(img);
      }
      else if (this.options.discountType === 'percentage' && tier.discount > 0) {
        marker.setAttribute('data-discount', tier.discount);
      }

      this.thresholdsContainer.appendChild(marker);
    });
  }

  /**
   * Resolves the include and exclude lists to product id sets. Liquid only exposes 50 products per collection, so the
   * collection product ids come from /collections/<handle>/products.json instead (250 per page), cached in sessionStorage
   * per collection handle and shared between the bar instances.
   * - eligibleProductIds: included collections + included products; stays null (every product counts) without include lists,
   *   or when none of the included collections could be loaded (console warning).
   * - excludedProductIds: excluded products + excluded collections (a collection that fails to load is skipped, with a warning).
   */
  async resolveEligibleProducts() {
    const [includedSets, excludedSets] = await Promise.all([
      Promise.all(this.collectionsToInclude.map(handle => CartProgressBar.fetchCollectionProductIds(handle))),
      Promise.all(this.collectionsToExclude.map(handle => CartProgressBar.fetchCollectionProductIds(handle))),
    ]);

    if (this.collectionsToInclude.length || this.productIdsToInclude.length) {
      const loadedIncludes = includedSets.filter(Boolean);
      if (this.collectionsToInclude.length && !loadedIncludes.length && !this.productIdsToInclude.length) {
        console.warn('Cart progress bar: none of the collections to include could be loaded, every product counts', this.collectionsToInclude);
      }
      else {
        this.eligibleProductIds = new Set([...this.productIdsToInclude, ...loadedIncludes.flatMap(productIds => [...productIds])]);
      }
    }

    excludedSets.forEach((productIds, index) => {
      if (!productIds) {
        console.warn(`Cart progress bar: the collection to exclude "${this.collectionsToExclude[index]}" could not be loaded and is not excluded`);
        return;
      }
      productIds.forEach(productId => this.excludedProductIds.add(productId));
    });
  }

  /**
   * @param {String} handle collection handle
   * @returns {Promise<Set<Number>|null>} product ids of the collection, null when the collection could not be loaded
   */
  static fetchCollectionProductIds(handle) {
    if (!CartProgressBar._collectionProducts.has(handle)) {
      const storageKey = `cart-progress-bar:collection-products:${handle}`;

      const load = async () => {
        try {
          const cached = JSON.parse(sessionStorage.getItem(storageKey));
          if (Array.isArray(cached)) return new Set(cached);
        }
        catch {/* sessionStorage unavailable */}

        const productIds = [];
        // products.json returns at most 250 products per page
        for (let page = 1; page <= 40; page++) {
          const response = await fetch(`${routes.collections_url}/${handle}/products.json?limit=250&page=${page}`);
          if (!response.ok) throw new Error(`${response.status} for collection "${handle}"`);
          const { products } = await response.json();
          products.forEach(product => productIds.push(product.id));
          if (products.length < 250) break;
        }

        try {
          sessionStorage.setItem(storageKey, JSON.stringify(productIds));
        }
        catch {/* sessionStorage unavailable */}

        return new Set(productIds);
      };

      CartProgressBar._collectionProducts.set(handle, load().catch(error => {
        console.error('Cart progress bar: error loading collection products:', error);
        return null;
      }));
    }

    return CartProgressBar._collectionProducts.get(handle);
  }

  /**
   * Evaluates every tier against the cart
   * @param {Object} cart
   * @returns {Array<{total: Number, thresholdReached: Boolean, discountApplied: Boolean, reached: Boolean}>} one state per tier
   */
  getTierStates(cart) {
    return this.tiers.map(tier => {
      const total = this.getTierTotal(tier, cart);
      const thresholdReached = total >= tier.goal;
      const discountApplied = this.isTierDiscountApplied(tier, cart);
      return { total, thresholdReached, discountApplied, reached: thresholdReached && discountApplied };
    });
  }

  getCompletedTierIndex(tierStates) {
    return tierStates.findLastIndex(tierState => tierState.reached);
  }

  /**
   * Progress over the whole bar in percent: the completed tiers plus the part of the next tier that is reached,
   * measured on the total of that next tier (which may count other products than the completed tier)
   */
  getProgress(tierStates, completedTierIndex) {
    if (completedTierIndex >= this.tiers.length - 1) return 100;

    const nextTierIndex = completedTierIndex + 1;
    const tierStart = completedTierIndex < 0 ? 0 : this.tiers[completedTierIndex].goal;
    const tierRange = this.tiers[nextTierIndex].goal - tierStart;
    const nextTierTotal = tierStates[nextTierIndex].total;
    const progressInTier = tierRange > 0 ? Math.min(Math.max(nextTierTotal - tierStart, 0), tierRange) / tierRange : 0;

    return (nextTierIndex + progressInTier) * this.tierWidthPercent;
  }

  getTierProductId(tier) {
    if (!tier || tier.freeShipping || this.options.discountType !== 'product') return null;
    return tier.productId || null;
  }

  /**
   * Free gifts (tier products and gifted items) never count towards the tiers
   */
  isFreeProduct(item) {
    const freeProduct = item.properties?._free_product;
    return freeProduct === true || freeProduct === 'true' || !!item.properties?._Gifted;
  }

  /**
   * Line price that counts towards the tiers: the original line price minus the bundle discounts (Pick & Mix, paste
   * packs), calculated from the line item properties (same source as cart-subtotals.liquid). Whether Shopify already
   * applied those bundle discounts in the cart or only does so in checkout makes no difference, the outcome is the same.
   * Other discounts Shopify applied (e.g. the tier discount itself once the threshold is reached) do not lower the price:
   * Shopify evaluates a threshold on the price before the discount it unlocks, and the bar must not un-reach a tier
   * right after it is reached.
   * @param {Object} item cart line item
   * @returns {Number} line price in cents after the bundle discount
   */
  getDiscountedLinePrice(item) {
    const properties = item.properties || {};

    const pickMixPercentage = parseFloat(properties._pickMix) || 0;
    if (pickMixPercentage > 0) {
      return Math.round(item.original_line_price * (100 - pickMixPercentage) / 100);
    }

    const packDiscountPerItem = parseInt(properties._packDiscountPerItem, 10) || 0;
    if (packDiscountPerItem > 0) {
      return Math.max(item.original_line_price - packDiscountPerItem * item.quantity, 0);
    }

    return item.original_line_price;
  }

  /**
   * The cart:updated event is also dispatched with the response of /cart/add.js, which only contains the added items
   * @param {Object} cartData
   * @returns {Boolean} true when the data is a full cart object
   */
  isCart(cartData) {
    return !!cartData && Array.isArray(cartData.items) && typeof cartData.original_total_price === 'number';
  }

  async updateProgress(cartData = null) {
    try {
      const cart = this.isCart(cartData) ? cartData : await this.fetchCart();
      if (!cart) return;

      const tierStates = this.getTierStates(cart);
      const completedTierIndex = this.getCompletedTierIndex(tierStates);

      const updatedCart = await this.handleFreeProducts(completedTierIndex, cart);
      if (updatedCart) {
        // Free products were added or removed: every progress bar (and the header) re-renders with the fresh cart
        window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: updatedCart }));
        if (this._initialized) this.updateCartUI();
        return;
      }

      this.updateBar(tierStates, completedTierIndex);
      this.updateTextDisplay(tierStates, completedTierIndex);
    }
    catch (error) {
      console.error('Error updating cart progress:', error);
    }
  }

  async fetchCart() {
    const response = await fetch(window.routes.cart_url);
    return response.ok ? response.json() : null;
  }

  /**
   * Cart total that counts towards a tier: only the eligible products (every product without include lists),
   * without free gifts and excluded products, at the line price after the bundle discounts (see getDiscountedLinePrice).
   * Free shipping tiers always count the whole cart: Shopify's free shipping threshold is on the order, not on a collection.
   * @param {Object} tier
   * @param {Object} cart
   * @returns {Number} total in cents
   */
  getTierTotal(tier, cart) {
    const eligibleProductIds = tier.freeShipping ? null : this.eligibleProductIds;

    return cart.items.reduce((sum, item) => {
      if (this.isFreeProduct(item) || this.excludedProductIds.has(item.product_id)) return sum;
      if (eligibleProductIds && !eligibleProductIds.has(item.product_id)) return sum;
      return sum + this.getDiscountedLinePrice(item);
    }, 0);
  }

  /**
   * @returns {Number} amount in cents that the discount with this title takes off the line, 0 without a title or match
   */
  getDiscountAllocation(item, discountTitle) {
    if (!discountTitle) return 0;

    return (item.line_level_discount_allocations || []).reduce((sum, allocation) => {
      const title = CartProgressBar.normalizeDiscountTitle(allocation.discount_application?.title);
      return title === discountTitle ? sum + (allocation.amount || 0) : sum;
    }, 0);
  }

  /**
   * Without a configured discount title the threshold alone decides. With a title, the discount must be present in the
   * cart: on a line (product discount) or on the cart (order discount), both as Shopify reports them in cart.js.
   */
  isTierDiscountApplied(tier, cart) {
    if (!tier.discountTitle) return true;

    const cartLevelTitles = (cart.cart_level_discount_applications || []).map(application => CartProgressBar.normalizeDiscountTitle(application.title));
    if (cartLevelTitles.includes(tier.discountTitle)) return true;

    return cart.items.some(item => this.getDiscountAllocation(item, tier.discountTitle) > 0);
  }

  /**
   * Adds the free products of the reached tiers and removes the ones that are no longer earned
   * @returns {Object|null} the updated cart when products were added or removed, otherwise null
   */
  async handleFreeProducts(completedTierIndex, cart) {
    if (this.options.discountType !== 'product' || CartProgressBar._processingFreeProducts) return null;

    const rewardsType = this.options.rewardsType || 'exclusive';
    // Free gifts from the free_gift metaobjects (_free_gift property) belong to free-gifts.js, not to the tiers
    const freeProductsInCart = cart.items.filter(item => this.isFreeProduct(item) && !item.properties?._free_gift);
    const matchedKeys = new Set();

    const productsToAdd = [];
    const productsToRemove = [];

    this.tiers.forEach((tier, tierIndex) => {
      const productId = this.getTierProductId(tier);
      if (!productId) return;

      const isEligible = this.isTierEligible(tierIndex, completedTierIndex, rewardsType);
      const productInCart = freeProductsInCart.find(item => String(item.variant_id) === String(productId));

      if (productInCart) matchedKeys.add(productInCart.key);

      if (isEligible && !productInCart) {
        productsToAdd.push(productId);
      }
      else if (!isEligible && productInCart) {
        productsToRemove.push(productInCart);
      }
    });

    // Free products that no longer belong to any tier
    freeProductsInCart.forEach(item => {
      if (!matchedKeys.has(item.key)) productsToRemove.push(item);
    });

    if (!productsToRemove.length && !productsToAdd.length) return null;

    CartProgressBar._processingFreeProducts = true;
    try {
      let success = true;
      if (productsToRemove.length) success = (await this.removeFreeProducts(productsToRemove)) && success;
      if (productsToAdd.length) success = (await this.addFreeProducts(productsToAdd)) && success;
      if (!success) return null;
      return await this.fetchCart();
    }
    finally {
      CartProgressBar._processingFreeProducts = false;
    }
  }

  isTierEligible(tierIndex, completedTierIndex, rewardsType) {
    if (completedTierIndex < 0 || tierIndex > completedTierIndex) return false;
    return rewardsType === 'cumulative' || tierIndex === completedTierIndex;
  }

  /**
   * The fill and the single tier marker follow --progress (see the stylesheet in snippets/cart-progress-bar.liquid)
   */
  updateBar(tierStates, completedTierIndex) {
    const progress = this.getProgress(tierStates, completedTierIndex);

    this.style.setProperty('--progress', `${progress}%`);
    this.track?.setAttribute('aria-valuenow', Math.round(progress));

    this.thresholdsContainer?.querySelectorAll('[data-threshold]').forEach((marker, index) => {
      marker.classList.toggle('cart-progress-bar__threshold--reached', !!tierStates[index]?.reached);
    });
  }

  updateTextDisplay(tierStates, completedTierIndex) {
    if (!this.nextTierText) return;

    const currentTier = this.tiers[completedTierIndex] ?? null;
    const nextTier = this.tiers[completedTierIndex + 1] ?? null;

    if (!nextTier) {
      this.nextTierText.textContent = this.buildReachedMessage(currentTier);
      return;
    }

    // The amount needed is measured on the next tier's own total (its eligible products); never below zero, which
    // happens when the threshold is met but Shopify has not applied the configured discount (yet)
    const amountNeeded = Math.max(nextTier.goal - tierStates[completedTierIndex + 1].total, 0);
    const amountNeededHtml = `<span class="cart-progress-bar__amount-needed">${formatMoneyShort(amountNeeded)}</span>`;
    const nextMessage = this.buildNextTierMessage(nextTier, amountNeededHtml);

    this.nextTierText.innerHTML = currentTier
      ? [this.buildReachedMessage(currentTier), nextMessage].filter(Boolean).join(' ')
      : nextMessage;
  }

  buildReachedMessage(tier) {
    if (!tier) return '';
    if (tier.freeShipping) return this.options.textLabelsFreeShippingReached;
    if (this.options.discountType === 'product' && tier.productName) {
      return this.options.textLabelsFreeProductReached.replace('[[product]]', tier.productName);
    }
    if (this.options.discountType === 'percentage' && tier.discount > 0) {
      return this.options.textLabelsMaximumTierReached.replace('[[max]]', tier.discount);
    }
    return '';
  }

  buildNextTierMessage(tier, amountNeededHtml) {
    if (tier.freeShipping) {
      return this.options.textLabelsFreeShippingNeededForNextTier.replace('[[amount_needed]]', amountNeededHtml);
    }
    if (this.options.discountType === 'product') {
      return this.options.textLabelsProductNeededForNextTier
        .replace('[[amount_needed]]', amountNeededHtml)
        .replace('[[product]]', tier.productName ?? '');
    }
    if (this.options.discountType === 'percentage' && tier.discount > 0) {
      return this.options.textLabelsNeededForNextTier
        .replace('[[next_tier]]', tier.discount)
        .replace('[[amount_needed]]', amountNeededHtml);
    }
    return '';
  }

  async removeFreeProducts(items) {
    try {
      const updates = Object.fromEntries(items.map(item => [item.key, 0]));
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ updates }) });
      if (!response.ok) throw new Error(response.status);
      return true;
    }
    catch (error) {
      console.error('Error removing free products:', error);
      return false;
    }
  }

  async addFreeProducts(productIds) {
    try {
      const items = productIds.map(productId => ({
        id: productId,
        quantity: 1,
        properties: { _free_product: true },
      }));

      const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body: JSON.stringify({ items }) });
      if (!response.ok) throw new Error(response.status);
      return true;
    }
    catch (error) {
      console.error('Error adding free products:', error);
      return false;
    }
  }

  updateCartUI() {
    if (!CartProgressBar._debouncedRenderCart) {
      CartProgressBar._debouncedRenderCart = debounce(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartItems = document.querySelector('cart-items');
        if (cartDrawer) {
          cartDrawer.renderCartDrawer();
        }
        else if (cartItems) {
          fetch(window.routes.cart_url)
            .then(response => response.ok ? response.json() : null)
            .then(cart => cart && cartItems.updateContent(cart, null, null, true));
        }
      }, 150);
    }
    CartProgressBar._debouncedRenderCart();
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}
